import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useGroups } from '@/hooks/useGroupQueries';
import {
  useContentPermissions,
  useGrantContentPermission,
  useRevokeContentPermission,
  useSetContentPermissionLevel,
} from '@/hooks/usePermissionQueries';
import { cn } from '@/lib/utils';
import { AccessLevel, ContentPermission, GroupListItem } from '@vivport/backend/types';
import { Trash2, Users2 } from 'lucide-react';
import { useState } from 'react';

interface ContentSharingPanelProps {
  contentId: number;
  canEdit: boolean;
}

const ACCESS_LEVEL_OPTIONS: AccessLevel[] = [
  AccessLevel.VIEWER,
  AccessLevel.COMMENTER,
  AccessLevel.EDITOR,
  AccessLevel.OWNER,
  AccessLevel.CUSTODIAN,
];

const accessLevelLabel = (level: AccessLevel): string => {
  return level.charAt(0) + level.slice(1).toLowerCase();
};

export function ContentSharingPanel({ contentId, canEdit }: ContentSharingPanelProps) {
  const permissionsQuery = useContentPermissions(contentId);
  const groupsQuery = useGroups();
  const grantMutation = useGrantContentPermission();
  const revokeMutation = useRevokeContentPermission();
  const setLevelMutation = useSetContentPermissionLevel();

  const [pickedGroupId, setPickedGroupId] = useState<number | null>(null);
  const [pickedLevel, setPickedLevel] = useState<AccessLevel>(AccessLevel.VIEWER);
  const [error, setError] = useState<string | null>(null);

  const permissions = permissionsQuery.data?.items ?? [];
  const groups = groupsQuery.data?.items ?? [];

  const sharedGroupIds = new Set(permissions.map((p) => p.groupId));
  const availableGroups: GroupListItem[] = groups.filter((g) => !sharedGroupIds.has(g.id));

  const errorMessageFrom = (err: unknown): string => {
    if (typeof err === 'object' && err !== null) {
      const candidate = err as { response?: { data?: { message?: unknown } }; message?: unknown };
      const apiMessage = candidate.response?.data?.message;
      if (typeof apiMessage === 'string' && apiMessage.length > 0) return apiMessage;
      if (typeof candidate.message === 'string' && candidate.message.length > 0) return candidate.message;
    }
    return 'Unable to update sharing. Please try again.';
  };

  const handleGrant = () => {
    setError(null);
    if (pickedGroupId === null) return;
    grantMutation.mutate(
      { contentId, input: { groupId: pickedGroupId, accessLevel: pickedLevel } },
      {
        onSuccess: () => {
          setPickedGroupId(null);
          setPickedLevel(AccessLevel.VIEWER);
        },
        onError: (err) => setError(errorMessageFrom(err)),
      },
    );
  };

  const handleSetLevel = (groupId: number, accessLevel: AccessLevel) => {
    setError(null);
    setLevelMutation.mutate(
      { contentId, groupId, input: { accessLevel } },
      { onError: (err) => setError(errorMessageFrom(err)) },
    );
  };

  const handleRevoke = (groupId: number) => {
    setError(null);
    revokeMutation.mutate(
      { contentId, groupId },
      { onError: (err) => setError(errorMessageFrom(err)) },
    );
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <Users2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          Shared with
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {permissionsQuery.isLoading ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : permissions.length === 0 ? (
          <p className="text-xs text-muted-foreground">No one yet.</p>
        ) : (
          <ul className="divide-y">
            {permissions.map((permission) => (
              <SharingRow
                key={permission.groupId}
                permission={permission}
                canEdit={canEdit}
                onChangeLevel={(level) => handleSetLevel(permission.groupId, level)}
                onRevoke={() => handleRevoke(permission.groupId)}
                disabled={setLevelMutation.isPending || revokeMutation.isPending}
              />
            ))}
          </ul>
        )}

        {canEdit && (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Select
              value={pickedGroupId !== null ? String(pickedGroupId) : ''}
              onValueChange={(v) => setPickedGroupId(v ? Number(v) : null)}
              disabled={availableGroups.length === 0 || grantMutation.isPending}
            >
              <SelectTrigger className="h-8 min-w-[10rem] flex-1 text-xs">
                <SelectValue placeholder="Share with…" />
              </SelectTrigger>
              <SelectContent>
                {availableGroups.map((g) => (
                  <SelectItem key={g.id} value={String(g.id)}>
                    {g.name}
                    {!g.isSelf && ` · ${g.memberCount} member${g.memberCount === 1 ? '' : 's'}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={pickedLevel}
              onValueChange={(v) => setPickedLevel(v as AccessLevel)}
              disabled={grantMutation.isPending}
            >
              <SelectTrigger className="h-8 w-[8rem] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ACCESS_LEVEL_OPTIONS.map((level) => (
                  <SelectItem key={level} value={level}>
                    {accessLevelLabel(level)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              size="sm"
              variant="outline"
              onClick={handleGrant}
              disabled={pickedGroupId === null || grantMutation.isPending}
            >
              Share
            </Button>
          </div>
        )}

        {error && (
          <p className="text-xs text-destructive" role="alert">
            {error}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

interface SharingRowProps {
  permission: ContentPermission;
  canEdit: boolean;
  disabled: boolean;
  onChangeLevel: (level: AccessLevel) => void;
  onRevoke: () => void;
}

function SharingRow({ permission, canEdit, disabled, onChangeLevel, onRevoke }: SharingRowProps) {
  const memberLabel = permission.groupIsSelf
    ? permission.members[0]
      ? `${permission.members[0].firstName ?? ''} ${permission.members[0].surname ?? ''}`.trim() ||
        permission.members[0].email
      : permission.groupName
    : permission.groupName;

  return (
    <li className="flex items-center justify-between gap-2 py-2">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{memberLabel}</p>
        {!permission.groupIsSelf && (
          <Badge variant="secondary" className="mt-0.5 text-[10px]">
            {permission.memberCount} member{permission.memberCount === 1 ? '' : 's'}
          </Badge>
        )}
      </div>
      <div className="flex items-center gap-1">
        <Select
          value={permission.accessLevel}
          onValueChange={(v) => onChangeLevel(v as AccessLevel)}
          disabled={!canEdit || disabled}
        >
          <SelectTrigger className={cn('h-8 w-[8rem] text-xs')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ACCESS_LEVEL_OPTIONS.map((level) => (
              <SelectItem key={level} value={level}>
                {accessLevelLabel(level)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={onRevoke}
          disabled={!canEdit || disabled}
          aria-label="Remove access"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </li>
  );
}
